import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';

/**
 * MuiDropdownItem - A className-based implementation that matches the Bulma DropdownItem
 *
 * This component provides the same API as the previous MuiDropdownItem for easy migration
 */
const MuiDropdownItem = ({
  children,
  tag,
  to,
  onClick,
  className,
  isActive,
  ...rest
}) => {
  // Build the class names based on props
  const itemClasses = classNames('dropdown-item', {
    'is-active': isActive,
    [className]: className,
  });

  // Handle different tag types (NavLink, a, etc.)
  const { role = null, id = null, href = null, ...domProps } = rest;

  // <a href=""> points at the current URL, so the browser would reload the
  // page right after onClick runs. Only emit an href for a real destination.
  const linkHref = href || to || undefined;

  if (tag === NavLink && to) {
    return (
      <NavLink
        to={to}
        className={itemClasses}
        onClick={onClick}
        {...domProps}
      >
        {children}
      </NavLink>
    );
  }

  if (tag === 'a' || linkHref) {
    return (
      <a
          href={linkHref}
          className={itemClasses}
          onClick={onClick}
          {...domProps}
        >
          {children}
        </a>
    );
  }

  // Default case - render as a div
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={itemClasses}
      onClick={onClick}
      {...domProps}
    >
      {children}
    </div>
  );
};

MuiDropdownItem.propTypes = {
  children: PropTypes.node.isRequired,
  tag: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  to: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  isActive: PropTypes.bool,
};

MuiDropdownItem.defaultProps = {
  tag: null,
  to: '',
  onClick: () => {},
  className: '',
  isActive: false,
};

export default MuiDropdownItem;
